import { HttpQuery } from "@/interfaces/httpQuery";
import { Policy, PolicyToSend, Role } from "@/interfaces/roles";
import { EventBus, store } from "@/internal";
import HttpService from "@/services/http.service";
import { messagesService } from "@/services/messages.service";
import camelCase from "lodash/camelCase";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import set from "lodash/set";
import Vue from "vue";

const parsePolicies = (policies: Policy[]) => {
  let tag: string;
  let group: string;
  return policies.reduce((acc: { [key: string]: any }, p: Policy) => {
    if (p.tag && tag !== p.tag) {
      tag = camelCase(p.tag);
    }

    if (p.policy_group && group !== p.policy_group) {
      group = camelCase(p.policy_group);
    }
    set(acc, [tag, group, camelCase(p.name)], p);
    return acc;
  }, {});
};

class PermissionRoleService extends HttpService {
  constructor() {
    super();
  }

  get currentUser() {
    return store.getters["authModule/user"];
  }

  public async getRoles(query?: { [key: string]: string | boolean }) {
    try {
      this.uri = "/permission/roles";
      this.query.embed = "users";
      query = { ...query, no_pagination: true };
      const resp = await super.get(query);
      return resp.data;
    } catch (e) {
      messagesService.renderErrorMessage(e);
    } finally {
      if (this.query["q[name_contains]"]) {
        delete this.query["q[name_contains]"];
      }
    }
  }

  public async findRole(id: number) {
    try {
      // Request 1: role info
      // Request 2: policies of role
      const resp = await Promise.all([
        this.getRole(id),
        this.getRolePolicies(id)
      ]);
      const role = resp[0];
      role.policies = resp[1];
      return role;
    } catch (e) {
      messagesService.renderErrorMessage(e);
    } finally {
      delete this.query.embed;
    }
  }

  public async getRole(id: number) {
    this.uri = `/permission/roles/${id}`;
    const query: HttpQuery = {
      embed: "users"
    };
    const resp = await super.get(query);
    return resp.data;
  }

  public async getRolePolicies(id: number) {
    this.uri = `/permission/roles/${id}/policies`;
    this.query.embed = "locations";
    this.query.no_pagination = true;
    const resp = await super.get(this.query);
    delete this.query.no_pagination;
    return resp.data;
  }

  public async locationsByUser(userId: number) {
    this.uri = `/permission/users/${userId}/locations`;
    delete this.query.embed;
    const resp = await super.get(this.query);
    return resp.data.data;
  }

  public async policiesByUser(id: number) {
    this.uri = `/permission/users/${id}/policies`;
    delete this.query.embed;
    const query = { no_pagination: true };
    const resp = await this.get(query);
    return parsePolicies(resp.data);
  }

  public async rolesByUser(id: number) {
    this.uri = `/permission/users/${id}/roles`;
    const query = { no_pagination: true };
    const resp = await this.get(query);
    return resp.data;
  }

  public async updateRolesByUser(id: number, roles: number[]) {
    const resp = await Vue.axios({
      method: "PUT",
      url: `/permission/users/${id}/roles/sync`,
      data: roles
    });
    return resp.data;
  }

  public async servicecByAccount(accountId: number) {
    this.uri = `/accounts/${accountId}`;
    const query = { ...this.query, embed: "services" };
    const resp = await this.get(query);
    return resp.data;
  }

  public async saveRole(role: Role, currentRole: Role | null) {
    this.uri = `/permission/roles`;
    try {
      let resp;
      if (role.id) {
        resp = await this.put(role, {
          name: role.name,
          desc: role.desc
        });
      } else {
        resp = await this.post({
          name: role.name,
          desc: role.desc
        });
      }
      // Sync users
      if (
        !currentRole ||
        (currentRole && currentRole.users.length !== role.users.length) ||
        !isEqual(
          role.users.map(u =>
            u.delete ? { id: u.id, delete: u.delete } : { id: u.id }
          ),
          currentRole.users.map(u => ({ id: u.id }))
        )
      ) {
        const usersFormated = role.users
          .filter(user => !user.delete)
          .map(user => user.id);
        await this.syncUsers(resp.data.id, usersFormated as number[]);
      }

      // Sync Policies
      if (
        !currentRole ||
        (currentRole && currentRole.policies.length !== role.policies.length) ||
        !isEqual(currentRole.policies, role.policies)
      ) {
        await this.syncPolicies(resp.data.id, cloneDeep(role.policies));
      }
      messagesService.renderSuccessMessage(
        `role_mgmt.${role.id ? "role_update_success" : "role_save_success"}`
      );
    } catch (e) {
      messagesService.renderErrorMessage(e);
    } finally {
      EventBus.$emit("route", { name: "permission-management-list" });
    }
  }

  public async syncUsers(id: number, users: number[]) {
    const resp = await Vue.axios({
      method: "PUT",
      url: `/permission/roles/${id}/users/sync`,
      data: users
    });
    return resp.data;
  }

  public async syncPolicies(
    id: number,
    policies: Array<PolicyToSend | number>
  ) {
    const resp = await Vue.axios({
      method: "PUT",
      url: `/permission/roles/${id}/policies/sync`,
      data: policies
    });
    return resp.data;
  }
}

export const permissionRoleService = new PermissionRoleService();
